import { Link } from 'react-router-dom';
import { ClipboardList, MapPin, ChevronRight } from 'lucide-react';
import { timeAgo } from '../../lib/dates';
import type { Report, ReportCategory, ReportStatus } from '../../types';
import type { SectionProps } from './useSectionQuery';
import { SectionShell } from './SectionShell';

const CATEGORY_LABEL: Record<ReportCategory, string> = {
    roads: 'Roads & traffic',
    water: 'Water & sewer',
    streetlight: 'Streetlight',
    vandalism: 'Vandalism',
    electricity: 'Electricity',
    weeds: 'Weeds / verge',
    litter: 'Litter / dumping',
    dangerous_situation: 'Dangerous situation',
    fallen_tree: 'Fallen tree',
    obstruction: 'Obstruction',
    other: 'Other (FTRA)',
};

const STATUS_LABEL: Record<ReportStatus, string> = {
    reported: 'Reported',
    investigating: 'Investigating',
    in_progress: 'In progress',
    referred: 'Referred',
    resolved: 'Resolved',
};

function categoryLabel(c: ReportCategory | string): string {
    return CATEGORY_LABEL[c as ReportCategory] ?? c.replace(/_/g, ' ');
}

interface MyReportsProps extends Omit<SectionProps<Report[]>, 'data'> {
    /** The resident's open reports, newest first. */
    open: Report[] | null;
    /** Their reports fixed in the last month, most recently fixed first. */
    fixes: Report[] | null;
}

/** Rows on Home. Open ones take the room first; "All reports" has the rest. */
const MY_REPORTS_ROWS = 5;

/**
 * One block for the resident's own reports: what is still open, then what was
 * fixed lately. It replaced "My open reports" and a suburb-wide "Recent fixes",
 * which between them took two headers' worth of Home for what is one question
 * — where do my reports stand?
 */
export function MyReportsSection({ open, fixes, loading, error, onRetry }: MyReportsProps) {
    const failed = loading ? null : error ?? (open === null && fixes === null ? "Couldn't load this right now." : null);
    const openRows = (open ?? []).slice(0, MY_REPORTS_ROWS);
    const fixRows = (fixes ?? []).slice(0, MY_REPORTS_ROWS - openRows.length);
    return (
        <SectionShell title="My reports" icon={<ClipboardList size={20} aria-hidden="true" />} linkTo="/report" linkLabel="All reports" loading={loading} error={failed} onRetry={onRetry}>
            {openRows.length === 0 && fixRows.length === 0 ? (
                <div className="dash-quiet card">
                    <ClipboardList size={20} aria-hidden="true" />
                    <span>You have no open reports. <Link to="/report">Report an issue</Link></span>
                </div>
            ) : (
                <div className="issues-list">
                    {openRows.map((r) => (
                        <Link key={r.id} to={`/report/${r.id}`} className="issue-item card">
                            <div className="issue-info">
                                <strong>{categoryLabel(r.category)}</strong>
                                <span><MapPin size={12} aria-hidden="true" /> {r.location}</span>
                            </div>
                            <span className={`status-pill ${r.status}`}>{STATUS_LABEL[r.status] ?? r.status}</span>
                            <ChevronRight size={16} className="issue-arrow" aria-hidden="true" />
                        </Link>
                    ))}
                    {fixRows.map((r) => (
                        <Link key={r.id} to={`/report/${r.id}`} className="issue-item card fix-item">
                            {r.resolution_thumb_url && (
                                <img src={r.resolution_thumb_url} alt="" className="fix-thumb" loading="lazy" />
                            )}
                            <div className="issue-info">
                                <strong>{categoryLabel(r.category)}</strong>
                                <span><MapPin size={12} aria-hidden="true" /> {r.location}</span>
                                <span className="fix-time">Fixed {timeAgo(r.resolved_at ?? r.created_at)}</span>
                            </div>
                            <span className="status-pill resolved">Resolved</span>
                        </Link>
                    ))}
                </div>
            )}
        </SectionShell>
    );
}
